import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WatchlistContext = createContext(null);

export function WatchlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/watchlist");
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const has = useCallback(
    (kind, id) => items.some((x) => x.item_kind === kind && x.item_id === id),
    [items],
  );

  const add = async (kind, id, city, note = "") => {
    await api.post("/watchlist", { item_kind: kind, item_id: id, city, note });
    // re-fetch to get enriched snapshot
    await refresh();
  };

  const removeByItem = async (kind, id) => {
    await api.delete(`/watchlist/by-item/${kind}/${id}`);
    setItems((prev) => prev.filter((x) => !(x.item_kind === kind && x.item_id === id)));
  };

  const removeById = async (watchId) => {
    await api.delete(`/watchlist/${watchId}`);
    setItems((prev) => prev.filter((x) => x.id !== watchId));
  };

  const toggle = async (kind, id, city) => {
    if (has(kind, id)) await removeByItem(kind, id);
    else await add(kind, id, city);
  };

  const value = useMemo(
    () => ({ items, loading, has, add, removeByItem, removeById, toggle, refresh }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, loading, has, refresh],
  );

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
}

export const useWatchlist = () => useContext(WatchlistContext);
